import { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../../PageHeading";
import ImageUploader from "../../ImageUploader";
import { IoClose } from "react-icons/io5";

const API_URL = "https://api.muktihospital.com/api/error-page";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const ErrorImage = () => {
  const [loading, setLoading] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [errorImage, setErrorImage] = useState("https://placehold.co/100");
  const [errorPageId, setErrorPageId] = useState("1");
  const [existingErrorPage, setExistingErrorPage] = useState(null);

  const breadcrumbs = [
    { label: "Dashboard", url: "/dashboard" },
    { label: "Website Setup"},
    { label: "Error Page" },
  ];

  useEffect(() => {
    const fetchErrorPage = async () => {
      setLoading(true);
      try {
        const response = await axios.get(API_URL, {
          headers: { "x-api-key": API_KEY },
        });
        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data.data && Array.isArray(response.data.data)
          ? response.data.data[0]
          : null;
        if (data) {
          setExistingErrorPage(data);
          setErrorImage(data.imageUrl || "https://placehold.co/100");
          setErrorPageId(data.id.toString());
        } else {
          setExistingErrorPage(null);
          setErrorImage("https://placehold.co/100");
          setErrorPageId("1");
        }
      } catch (error) {
        console.error("Error fetching ErrorPage:", error);
        toast.error(
          error.response?.status === 404
            ? "No ErrorPage found. You can create a new one."
            : `Failed to load ErrorPage data: ${error.message}`
        );
        setExistingErrorPage(null);
        setErrorImage("https://placehold.co/100");
        setErrorPageId("1");
      } finally {
        setLoading(false);
      }
    };
    fetchErrorPage();
  }, []);

  const handleImageUpload = (file) => {
    if (previewURL) {
      URL.revokeObjectURL(previewURL);
    }
    if (file instanceof File) {
      const fileURL = URL.createObjectURL(file);
      setErrorImage(fileURL);
      setPreviewURL(fileURL);
      setSelectedFile(file);
    } else {
      setErrorImage(existingErrorPage?.imageUrl || "https://placehold.co/100");
      setPreviewURL(null);
      setSelectedFile(null);
    }
  };

  const handleRemove = () => {
    if (previewURL) {
      URL.revokeObjectURL(previewURL);
    }
    setErrorImage(existingErrorPage?.imageUrl || "https://placehold.co/100");
    setPreviewURL(null);
    setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Please select an image to upload.");
      return;
    }
    if (!existingErrorPage && (!errorPageId || isNaN(parseInt(errorPageId)) || parseInt(errorPageId) <= 0)) {
      toast.error("Please provide a valid positive numeric ID for the new ErrorPage.");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("image", selectedFile);

    try {
      if (existingErrorPage) {
        const response = await axios.put(
          `${API_URL}/${existingErrorPage.id}/image`,
          formData,
          {
            headers: {
              "x-api-key": API_KEY,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        setErrorImage(response.data.newImageUrl);
        setExistingErrorPage({
          ...existingErrorPage,
          imageUrl: response.data.newImageUrl,
        });
        toast.success("ErrorPage image updated successfully!");
      } else {
        formData.append("id", errorPageId);
        const response = await axios.post(API_URL, formData, {
          headers: {
            "x-api-key": API_KEY,
            "Content-Type": "multipart/form-data",
          },
        });
        setExistingErrorPage(response.data.errorPage);
        setErrorImage(response.data.errorPage.imageUrl);
        toast.success("ErrorPage created successfully!");
      }
      setResetKey((prev) => prev + 1);
      setSelectedFile(null);
      setPreviewURL(null);
      setErrorPageId("1");
    } catch (error) {
      console.error("Error submitting ErrorPage:", error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.message);
      } else if (error.response?.status === 404) {
        toast.error("ErrorPage not found. Cannot update.");
      } else if (error.response?.status === 409) {
        toast.error(`ErrorPage with ID ${errorPageId} already exists. Please choose a different ID.`);
      } else {
        toast.error(`Failed to save ErrorPage: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ToastContainer />
      <PageHeading title="Error Page" breadcrumbs={breadcrumbs} />
      <div className="bg-white shadow-sm rounded-lg mt-5">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
          <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Error Content
          </h2>
        </div>
        <div className="p-5">
          <ImageUploader
            key={resetKey}
            src={errorImage}
            onFileUpload={handleImageUpload}
            disabled={loading}
          />
          {!selectedFile && (existingErrorPage || errorImage !== "https://placehold.co/100") && (
            <aside className="flex flex-wrap mt-4">
              <div className="relative inline-flex gap-3 items-center border border-gray-300 rounded-md w-full h-auto px-3 py-4">
                <div className="flex min-w-0 overflow-hidden">
                  <img
                    src={
                      errorImage.startsWith("/Uploads")
                        ? `https://api.muktihospital.com${errorImage}`
                        : errorImage
                    }
                    className="block w-14 border border-M-heading-color/10 rounded-md p-1"
                    alt="Error Page Image"
                  />
                </div>
                <div className="flex flex-col text-left flex-1">
                  <span className="text-sm text-M-text-color/60 font-inter font-semibold mb-1 truncate">
                    {existingErrorPage ? `ErrorPage-${existingErrorPage.id}` : "Placeholder Image"}
                  </span>
                  <span className="text-xs text-M-text-color font-inter font-medium">
                    Unknown size
                  </span>
                </div>
                {!existingErrorPage && (
                  <button
                    onClick={handleRemove}
                    className="ml-auto text-red-500 hover:text-red-700"
                    title="Remove image"
                  >
                    <IoClose size={20} />
                  </button>
                )}
              </div>
            </aside>
          )}
        </div>
      </div>
      <div className="mt-6 flex gap-4 justify-end">
        <button
          type="submit"
          onClick={handleSubmit}
          className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
          disabled={loading || (!existingErrorPage && !errorPageId)}
        >
          {loading ? "Processing..." : existingErrorPage ? "Update" : "Create"}
        </button>
      </div>
    </div>
  );
};

export default ErrorImage; 